import Modal from "components/Modal";
import Button from "components/Button";

type DuplicateEntry = { value: string; count: number };

// Parsing mirrors the list_literal check in hostedScrapingValidators:
// split on newlines, trim, drop blanks.
const parseEntries = (list: string): string[] =>
  list.split('\n').map((entry) => entry.trim()).filter(Boolean);

// The distinct values that appear more than once, with their total occurrences.
const findDuplicates = (list: string): DuplicateEntry[] => {
  const counts = new Map<string, number>();
  parseEntries(list).forEach((entry) => counts.set(entry, (counts.get(entry) ?? 0) + 1));
  return Array.from(counts.entries())
    .filter(([, count]) => count > 1)
    .map(([value, count]) => ({ value, count }));
};

// Unique entries, keeping the first occurrence and its original order.
export const dedupeList = (list: string): string =>
  Array.from(new Set(parseEntries(list))).join('\n');

interface IDuplicateEntriesModalProps {
  label: string;
  list: string;
  onRemove: () => void;
  onClose: () => void;
}

export const DuplicateEntriesModal = ({ label, list, onRemove, onClose }: IDuplicateEntriesModalProps) => {
  const duplicates = findDuplicates(list);
  const columnLabel = label.charAt(0).toUpperCase() + label.slice(1);

  return (
    <Modal headline={`Duplicate ${label}`} onClose={onClose} closeOnEscapeKey={true}>
      <div className="flex flex-col p-5 gap-y-6">
        <p className="text-gray dark:text-neutral-600">
          The following {label} appear more than once in your list. Removing duplicates keeps the first occurrence of each.
        </p>
        <div className="flex flex-col w-full max-h-96 overflow-auto text-gray dark:text-neutral-600">
          <table className="table-auto w-full border shrink grow-0 bg-lightestGray dark:bg-neutral-50">
            <thead>
              <tr className="h-6">
                <th className="px-4 py-2 border text-left">{columnLabel}</th>
                <th className="px-4 py-2 border w-px whitespace-nowrap">Count</th>
              </tr>
            </thead>
            <tbody>
              {duplicates.map((entry, index) => (
                <tr key={index} className="h-6">
                  <td className="border px-4 py-2 break-all">{entry.value}</td>
                  <td className="border px-4 py-2 text-center whitespace-nowrap">&times;{entry.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-x-4">
          <Button text="Close" className="button button-secondary" onClick={onClose} size="MD" />
          <Button text="Remove duplicates" className="button button-primary" onClick={onRemove} size="MD" />
        </div>
      </div>
    </Modal>
  );
};
